import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import FileUpload from "components/FileUpload/FileUpload";
import { addFactoringImages } from "./addFactoringImages";
import { useAuthenticatedUser } from "../../../AuthProvider";

interface ImageUploadDialogProps {
  open: boolean;
  applicationNumber: string;
  factoringId: string;
  onClose: () => void;
}

// Component implementation
function ImageUploadDialogComponent({
  open,
  applicationNumber,
  factoringId,
  onClose,
}: ImageUploadDialogProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [isUploaded, setIsUploaded] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const user = useAuthenticatedUser();

  const handleFilesChange = (newFiles: File[]) => {
    setFiles(newFiles);
  };

  const handleUpload = async () => {
    try {
      if (!user?.uid) {
        console.error("未認証のためアップロードできません。");
        return;
      }
      if (!factoringId) {
        console.error("factoringId が未指定のためアップロードできません。");
        return;
      }
      setIsUploading(true);
      await addFactoringImages(user.uid, factoringId, files);
      setIsUploaded(true);
    } catch (error) {
      console.error("画像のアップロード中にエラーが発生しました:", error);
    } finally {
      setIsUploading(false); // 成功・失敗に関わらずローディング状態を解除
    }
  };

  const handleClose = () => {
    // Reset state when dialog is closed
    setFiles([]);
    setIsUploaded(false);
    onClose();
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>申込 {applicationNumber} の画像アップロード</DialogTitle>
      <DialogContent>
        {!isUploaded ? (
          isUploading ? (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <CircularProgress size={40} />
              <Typography variant="body1" sx={{ mt: 2 }}>
                アップロード中...
              </Typography>
            </Box>
          ) : (
            <FileUpload
              onFilesChange={handleFilesChange}
              maxFiles={4}
              allowedExtensions={["jpg", "jpeg", "png", "pdf"]}
              files={files}
            />
          )
        ) : (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="h6" color="primary">
              アップロードが完了しました
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              画像のアップロードが正常に完了しました。
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!isUploaded ? (
          <>
            <Button onClick={handleClose} disabled={isUploading}>
              閉じる
            </Button>
            <Button
              onClick={handleUpload}
              variant="contained"
              disabled={files.length === 0 || isUploading}
            >
              アップロード
            </Button>
          </>
        ) : (
          <Button onClick={handleClose}>閉じる</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// Render hooks implementation
function useImageUploadDialog() {
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    factoringId: string;
    applicationNumber: string;
  }>({
    open: false,
    factoringId: "",
    applicationNumber: "",
  });

  const openDialog = (factoringId: string, applicationNumber: string) => {
    setDialogState({
      open: true,
      factoringId,
      applicationNumber,
    });
  };

  const closeDialog = () => {
    setDialogState((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // Render function that returns the dialog component
  const renderDialog = () => (
    <ImageUploadDialogComponent
      open={dialogState.open}
      factoringId={dialogState.factoringId}
      applicationNumber={dialogState.applicationNumber}
      onClose={closeDialog}
    />
  );

  return {
    openImageUploadDialog: openDialog,
    renderImageUploadDialog: renderDialog,
  };
}

export default useImageUploadDialog;
